import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { GridDataResult } from '@progress/kendo-angular-grid';
import { CompositeFilterDescriptor, FilterDescriptor, State } from '@progress/kendo-data-query';
import { ErrorParserService } from '../common-services/error-parser.service';

@Injectable({
  providedIn: 'root',
})
export class SupplierAPIService extends BehaviorSubject<GridDataResult> {
  public baseUrlLocal: string = environment.apiBaseUrl;
  public loading: boolean = false;
  public readonly errorMessage$ = new BehaviorSubject<string>('');

  private static readonly textFields: string[] = [
    'supplierId',
    'itemId',
    'itemDescription',
    'itemDetailedDescription',
    'supplierItemIdentification'
  ];

  // These codes are only 2 characters long, so the 3 character minimum does not apply.
  private static readonly shortTextFields: string[] = [
    'countryOfOriginCode',
    'stockCode'
  ];

  private static readonly exactFields: string[] = [
    'itemState',
    'isMainSupplier'
  ];

  constructor(private http: HttpClient, private errorParser: ErrorParserService) {
    super({ data: [], total: null });
  }

  public query(state: State, filter: CompositeFilterDescriptor): void {
    this.loading = true;
    this.errorMessage$.next('');
    this.fetch(state, filter).subscribe((x) => super.next(x));
  }

  protected fetch(state: State, filter: CompositeFilterDescriptor): Observable<GridDataResult> {
    const params: string[] = [
      `pageindex=${state.skip}`,
      `pagesize=${state.take}`
    ];

    for (const field of SupplierAPIService.textFields) {
      const value = this.getStateFilterValue(filter, field);
      if (value != undefined && value.length >= 3) {
        params.push(`${field}=${encodeURIComponent(value)}`);
      }
    }

    for (const field of SupplierAPIService.shortTextFields) {
      const value = this.getStateFilterValue(filter, field);
      if (value != undefined && value.length > 0) {
        params.push(`${field}=${encodeURIComponent(value)}`);
      }
    }

    for (const field of SupplierAPIService.exactFields) {
      const value = this.getStateFilterValue(filter, field);
      if (value != undefined && value.length > 0) {
        params.push(`${field}=${encodeURIComponent(value)}`);
      }
    }

    return this.http.get<GridDataResult>(`${this.baseUrlLocal}suppliers/search?${params.join('&')}`)
      .pipe(
        map((response: GridDataResult) => {
          return {
            data: response.data,
            total: response.total
          };
        }),
        catchError((error: HttpErrorResponse) => {
          this.loading = false;
          const parsed = this.errorParser.extractErrors(error);
          this.errorMessage$.next(parsed.errorMessage || `Failed to load suppliers. Status: ${error.status}`);
          return of({ data: [], total: 0 } as GridDataResult);
        }),
        tap(() => (this.loading = false))
      );
  }

  private getStateFilterValue(filter: CompositeFilterDescriptor, field: string): string | undefined {
    if (!filter?.filters?.length) {
      return undefined;
    }

    const matchingFilter = filter.filters.find((item): item is FilterDescriptor => {
      return !('filters' in item) && item.field === field;
    });

    if (matchingFilter?.value === undefined || matchingFilter.value === null) {
      return undefined;
    }

    return String(matchingFilter.value);
  }
}

@Injectable()
export class SuppliersService extends SupplierAPIService {
  constructor(http: HttpClient, errorParser: ErrorParserService) {
    super(http, errorParser);
  }
}
